import { Card } from '../Models/Card'
import { cardState, appState, getCurrentCardState } from './state'
import {
    foundationCardX,
    foundationCardY,
    tableauCardX,
    tableauCardY,
    tableauPileExtent,
    isLandscapeLayout,
    tableauCardStep,
} from './allXYCoordinates'
import { isPointInRect } from './getClickSelection'
import { getCardPosition } from './cardPositions'

/**
 * Compute the intersection area between two axis-aligned rectangles.
 * Returns 0 if they do not overlap.
 */
function rectOverlapArea(
    ax: number,
    ay: number,
    aw: number,
    ah: number,
    bx: number,
    by: number,
    bw: number,
    bh: number
): number {
    const overlapX = Math.max(0, Math.min(ax + aw, bx + bw) - Math.max(ax, bx))
    const overlapY = Math.max(0, Math.min(ay + ah, by + bh) - Math.max(ay, by))
    return overlapX * overlapY
}
export function cardIndexTableau(incCard: Card): [number, number] {
    const currentState = getCurrentCardState()
    for (let i = currentState.tableau.length - 1; i >= 0; i--) {
        if (currentState.tableau[i].length > 0) {
            for (let j = currentState.tableau[i].length - 1; j >= 0; j--) {
                const curCard = currentState.tableau[i][j]
                if (curCard && incCard && incCard.type === curCard.type && incCard.value === curCard.value) {
                    return [i, j]
                }
            }
        }
    }
}

export function cardIndexFoundation(incCard: Card): [number, number] {
    const currentState = getCurrentCardState()
    for (let i = currentState.foundation.length - 1; i >= 0; i--) {
        if (currentState.foundation[i].length > 0) {
            for (let j = currentState.foundation[i].length - 1; j >= 0; j--) {
                if (currentState.foundation[i][j]) {
                    const curCard = currentState.foundation[i][j]
                    if (
                        curCard &&
                        incCard &&
                        incCard.type === curCard.type &&
                        incCard.value === curCard.value
                    ) {
                        return [i, j]
                    }
                }
            }
        }
    }
}

export function cardIndexWaste(incCard: Card): number {
    const currentState = getCurrentCardState()
    for (let i = currentState.waste.length - 1; i >= 0; i--) {
        const curCard = currentState.waste[i]

        if (curCard && incCard && incCard.type === curCard.type && incCard.value === curCard.value) {
            return i
        }
    }
}

export function isCardOverFoundation(x: number, y: number): number {
    for (let i = 0; i < appState.NUM_FOUNDATIONS; i++) {
        if (
            isPointInRect(x, y, foundationCardX(i), foundationCardY(i), appState.cardWidth, appState.cardHeight)
        ) {
            return i
        }
    }
}

export function isCardOverTableau(x: number, y: number): number {
    const currentState = getCurrentCardState()
    const isLeftHanded = appState.settings.handedness === 'left'

    for (let i = 0; i < appState.NUM_TABLEAU; i++) {
        const baseX = tableauCardX(i)
        const baseY = tableauCardY(i)

        if (isLandscapeLayout()) {
            const pile = currentState.tableau[i]
            let extentY = 0
            if (pile.length > 0) {
                for (let j = 0; j < pile.length - 1; j++) {
                    extentY += tableauCardStep(pile[j].isFaceDown)
                }
            }
            const minX = baseX
            const maxX = baseX + appState.cardWidth
            const minY = baseY
            const maxY = baseY + appState.cardHeight + extentY

            if (x >= minX && x <= maxX && y >= minY && y <= maxY) {
                return i
            }
        } else {
            // Calculate the horizontal extent of this tableau column
            const pile = currentState.tableau[i]
            const extent = pile.length > 0 ? tableauPileExtent(pile) : 0
            const farEdgeX = isLeftHanded ? baseX + extent : baseX - extent

            // Determine the X bounds for this column
            const minX = isLeftHanded ? baseX : farEdgeX
            const maxX = isLeftHanded ? farEdgeX + appState.cardWidth : baseX + appState.cardWidth

            // Check if click is within the column bounds
            if (x >= minX && x <= maxX && y >= baseY && y <= baseY + appState.cardHeight) {
                return i
            }
        }
    }
}

// ─── Bounding-Box Overlap Drop Detection ──────────────────────────────
//
// These functions determine the drop target by computing the overlap area
// between the dragged card's bounding box and each candidate target.
// This is more accurate than point-based detection because the user's finger
// position can be offset from where the card visually sits on screen.

/**
 * Find the foundation pile that the dragged card(s) overlap the most.
 * Returns the foundation index (0-3), or undefined if no overlap.
 */
export function findOverlapFoundation(draggedCards: Card[]): number | undefined {
    if (draggedCards.length === 0) return undefined

    // Use the first (bottom) dragged card's rendered position as the bounding box
    const dragPos = getCardPosition(draggedCards[0])
    const dragX = dragPos.x
    const dragY = dragPos.y
    const cw = appState.cardWidth
    const ch = appState.cardHeight

    let bestIndex: number | undefined
    let bestArea = 0

    for (let i = 0; i < appState.NUM_FOUNDATIONS; i++) {
        const fx = foundationCardX(i)
        const fy = foundationCardY(i)
        const area = rectOverlapArea(dragX, dragY, cw, ch, fx, fy, cw, ch)
        if (area > bestArea) {
            bestArea = area
            bestIndex = i
        }
    }

    return bestIndex
}

/**
 * Find the tableau column that the dragged card(s) overlap the most.
 * Returns the tableau index (0-6), or undefined if no overlap.
 *
 * For each tableau column, the drop zone is the full column extent
 * (including cascaded cards), same as the point-based detection.
 */
export function findOverlapTableau(draggedCards: Card[]): number | undefined {
    if (draggedCards.length === 0) return undefined

    const currentState = getCurrentCardState()
    const isLeft = appState.settings.handedness === 'left'

    // Use the first (bottom) dragged card's rendered position
    const dragPos = getCardPosition(draggedCards[0])
    const dragX = dragPos.x
    const dragY = dragPos.y
    const cw = appState.cardWidth
    const ch = appState.cardHeight

    let bestIndex: number | undefined
    let bestArea = 0

    for (let i = 0; i < appState.NUM_TABLEAU; i++) {
        const baseX = tableauCardX(i)
        const baseY = tableauCardY(i)

        if (isLandscapeLayout()) {
            const pile = currentState.tableau[i]
            let extentY = 0
            if (pile.length > 0) {
                for (let j = 0; j < pile.length - 1; j++) {
                    extentY += tableauCardStep(pile[j].isFaceDown)
                }
            }
            const colMinX = baseX
            const colMinY = baseY
            const colWidth = cw
            const colHeight = ch + extentY

            const area = rectOverlapArea(dragX, dragY, cw, ch, colMinX, colMinY, colWidth, colHeight)
            if (area > bestArea) {
                bestArea = area
                bestIndex = i
            }
        } else {
            const pile = currentState.tableau[i]
            const extent = pile.length > 0 ? tableauPileExtent(pile) : 0
            const farEdgeX = isLeft ? baseX + extent : baseX - extent

            // Column bounding box
            const colMinX = isLeft ? baseX : farEdgeX
            const colMaxX = isLeft ? farEdgeX + cw : baseX + cw
            const colWidth = colMaxX - colMinX

            const area = rectOverlapArea(dragX, dragY, cw, ch, colMinX, baseY, colWidth, ch)
            if (area > bestArea) {
                bestArea = area
                bestIndex = i
            }
        }
    }

    return bestIndex
}
